import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Building2, Heart, Factory, Laptop } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'
import { MotionTokens } from '../styles/motion-tokens'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const sectionReveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: {
    duration: MotionTokens.sectionReveal.duration,
    ease: [0.22, 1, 0.32, 1],
  },
}

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

const SOLUTIONS = [
  {
    icon: Building2,
    title: 'Financial Services',
    desc: 'Revolutionize financial operations with AI-powered risk assessment, fraud detection, and regulatory compliance systems.',
    capabilities: [
      { name: 'Real-Time Risk Scoring', detail: 'Continuous monitoring and assessment of portfolio risk across all asset classes' },
      { name: 'Fraud Pattern Recognition', detail: 'Advanced anomaly detection identifying suspicious transactions in real-time' },
      { name: 'Regulatory Compliance', detail: 'Automated reporting and compliance monitoring for SEC, FINRA, Basel III' },
    ],
  },
  {
    icon: Heart,
    title: 'Healthcare',
    desc: 'Transform patient care with HIPAA-compliant clinical decision support and diagnostic assistance systems.',
    capabilities: [
      { name: 'Clinical Decision Support', detail: 'Evidence-based treatment recommendations integrated with EHR systems' },
      { name: 'Diagnostic Assistance', detail: 'Pattern recognition across medical imaging and patient data' },
      { name: 'Patient Outcome Prediction', detail: 'Personalized treatment planning based on comprehensive health data' },
    ],
  },
  {
    icon: Factory,
    title: 'Manufacturing',
    desc: 'Optimize production with predictive maintenance, quality control, and supply chain intelligence.',
    capabilities: [
      { name: 'Predictive Maintenance', detail: 'Equipment failure prediction reducing downtime by up to 50%' },
      { name: 'Quality Assurance', detail: 'Real-time defect detection with computer vision and sensor integration' },
      { name: 'Supply Chain Optimization', detail: 'Demand forecasting and inventory optimization across global operations' },
    ],
  },
  {
    icon: Laptop,
    title: 'Technology',
    desc: 'Accelerate software development with AI-assisted coding, architecture design, and security analysis.',
    capabilities: [
      { name: 'Code Generation', detail: 'Context-aware code completion and generation across 50+ programming languages' },
      { name: 'Architecture Patterns', detail: 'System design recommendations based on best practices and your codebase' },
      { name: 'Security Analysis', detail: 'Automated vulnerability detection and remediation suggestions' },
    ],
  },
]

export default function SolutionsPage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1
              className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
              style={brandFont}
            >
              Industry <span className="text-transparent bg-clip-text bg-trust-gradient">Solutions</span>
            </h1>
            <p className="text-xl text-white/70 max-w-4xl mx-auto" style={bodyFont}>
              Consciousness technology tailored to your industry&apos;s unique challenges and opportunities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {SOLUTIONS.map(({ icon: Icon, title, desc, capabilities }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-10">
                    <div className="flex items-center gap-4 mb-6">
                      <Icon className="w-12 h-12 text-trust-blue" strokeWidth={1.5} />
                      <h2 className="text-3xl tracking-wide text-trust-blue" style={{ ...bodyFont, fontWeight: 300 }}>
                        {title}
                      </h2>
                    </div>
                    <p className="text-white/70 mb-6 text-lg" style={bodyFont}>{desc}</p>
                    <h3 className="text-xl mb-4 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                      Key Capabilities
                    </h3>
                    <ul className="space-y-3 mb-6">
                      {capabilities.map((cap) => (
                        <li key={cap.name} className="flex items-start gap-3">
                          <span className="text-trust-blue mt-1">&#10003;</span>
                          <span className="text-white/70" style={bodyFont}>
                            <strong className="text-white" style={{ fontWeight: 400 }}>{cap.name}:</strong>{' '}
                            {cap.detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      className="px-6 py-2.5 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
                      style={bodyFont}
                    >
                      Learn More
                    </Link>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <GlassCard className="max-w-4xl mx-auto">
          <div className="p-12 text-center">
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Let&apos;s Build Your Solution
            </h2>
            <p className="text-xl text-white/70 mb-12" style={bodyFont}>
              Every industry has unique challenges. Let&apos;s discuss how LUKHAS can transform yours.
            </p>
            <Link
              to="/contact"
              className="px-12 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
              style={bodyFont}
            >
              Schedule a Consultation
            </Link>
          </div>
        </GlassCard>
      </section>

      <SiteFooter />
    </div>
  )
}
